import {useCallback, useMemo} from "react";

import {getAnalyticsSessionId} from "@pg-mono/algolytics";

import {notifyBugsnag} from "../../errors/bugsnag/notify_bugsnag";
import {addFailureNotification} from "../../notifications/utils/add_failure_notification";
import {ISelectedRegion} from "../../offer/list/actions/offer_list_selected_data_actions";
import {useAppDispatch, useAppSelector} from "../../store/hooks";
import {isRtkqError} from "../../store/utils/is_rtkq_error";
import {usePostSemanticSearchMutation} from "../api/post_semantic_search_api";
import {postSemanticSearchCacheKey} from "../constants/post_semantic_search_cache_key";
import {getSemanticSearchModalSize} from "../tracking/get_semantic_search_modal_size";
import {hitAiAssistantCta, hitAiAssistantOffer} from "../tracking/semantic_search_gtm_events";
import {trackAmplitudeSemanticSearchViewItemList} from "../tracking/track_amplitude_semantic_search_view_item_list";
import {trackAmplitudeSubmitAiQuestion} from "../tracking/track_amplitude_submit_ai_question";
import {GetSemanticSearchStoreResponseDto} from "../types/GetSemanticSearchStoreResponseDto";
import {PostSemanticSearchDataItemDto} from "../types/PostSemanticSearchDataItemDto";
import {SemanticSearchFormValues} from "../types/SemanticSearchFormValues";
import {useSemanticSearchLocalStorage} from "./use_semantic_search_local_storage";

const SEMANTIC_SEARCH_ERROR_MESSAGES: Record<number, string> = {
    403: "Nie masz dostępu do tej funkcjonalności. Skontaktuj się z administratorem.",
    422: "Zapytanie jest nieprawidłowe. Zmień treść i spróbuj ponownie.",
    500: "Wystąpił błąd wewnętrzny. Spróbuj ponownie później.",
    502: "Wyszukiwarka jest tymczasowo niedostępna. Spróbuj ponownie za chwilę."
};

const MAX_CONVERSATION_HISTORY = 20;

function getSemanticSearchErrorMessage(status: number): string {
    return SEMANTIC_SEARCH_ERROR_MESSAGES[status] ?? "Wystąpił błąd. Spróbuj ponownie.";
}

function convertToPostSemanticSearchResultsHistory(semanticSearchHistory: GetSemanticSearchStoreResponseDto | undefined): PostSemanticSearchDataItemDto[] {
    if (!semanticSearchHistory || semanticSearchHistory.length === 0) {
        return [];
    }

    const recentHistory = semanticSearchHistory.slice(-MAX_CONVERSATION_HISTORY);

    return recentHistory.map((item) => ({
        type: item.type,
        text: item.text,
        results: item.results?.length ? item.results.map((group) => group.map((property) => property.id)) : []
    }));
}

export function useSemanticSearchSubmit() {
    const dispatch = useAppDispatch();
    const selectedRegions = useAppSelector((state) => state.offerList.selectedRegions as ISelectedRegion[]);
    const regionIds = useMemo(() => selectedRegions.map((region) => region.id), [selectedRegions]);
    const modalSize = useAppSelector((state) => getSemanticSearchModalSize(state.semanticSearchModal.surfacePhase));

    const [postSemanticSearch, {isLoading: isPostSemanticSearchLoading}] = usePostSemanticSearchMutation({fixedCacheKey: postSemanticSearchCacheKey});
    const {history: semanticSearchHistory, isLoading: isSemanticSearchHistoryLoading, addMessage, clearHistory} = useSemanticSearchLocalStorage();

    const isLoading = isPostSemanticSearchLoading || isSemanticSearchHistoryLoading;

    const submitSemanticSearch = useCallback(
        async (data: SemanticSearchFormValues) => {
            const userQuery = data.userQuery.trim();

            if (isLoading || !userQuery) return;

            const analyticsSessionId = getAnalyticsSessionId();
            const semanticSearchResultsHistory = convertToPostSemanticSearchResultsHistory(semanticSearchHistory);

            if (typeof analyticsSessionId !== "string") {
                notifyBugsnag({message: "Semantic search missing analytics session id"}, "useSemanticSearchSubmit missing analytics session id", {
                    analyticsSessionId,
                    historyLength: semanticSearchHistory?.length ?? 0,
                    regionIds
                });
            }

            if (modalSize) {
                trackAmplitudeSubmitAiQuestion(modalSize, analyticsSessionId);
            }

            addMessage({
                type: "USER_INPUT",
                text: userQuery
            });

            const semanticSearchResult = await postSemanticSearch({
                // Casting because the API requires a string, while algolytics can return an empty value.
                analytic_session_id: analyticsSessionId as string,
                data: [
                    ...semanticSearchResultsHistory,
                    {
                        type: "USER_INPUT",
                        text: userQuery,
                        results: []
                    }
                ],
                regions: regionIds
            });

            if (semanticSearchResult.error && isRtkqError(semanticSearchResult.error)) {
                const status = typeof semanticSearchResult.error.status === "number" ? semanticSearchResult.error.status : 500;

                dispatch(addFailureNotification(getSemanticSearchErrorMessage(status)));

                if (status === 403) {
                    window.location.reload();
                }

                return;
            }

            if (semanticSearchResult.data) {
                const flatProperties = semanticSearchResult.data.properties.flat();

                flatProperties.forEach((property) => hitAiAssistantOffer("view", property));

                if (modalSize && flatProperties.length > 0) {
                    trackAmplitudeSemanticSearchViewItemList({properties: flatProperties, modalSize, analyticSessionId: analyticsSessionId});
                }

                if (semanticSearchResult.data.offer_signup) {
                    hitAiAssistantCta("view", "Załóż konto");
                    hitAiAssistantCta("view", "Zaloguj się");
                }

                addMessage({
                    type: "API_RESPONSE",
                    text: semanticSearchResult.data.response_text,
                    results: semanticSearchResult.data.properties,
                    offer_signup: semanticSearchResult.data.offer_signup,
                    filters: semanticSearchResult.data.filters,
                    count: semanticSearchResult.data.count
                });
            }
        },
        [addMessage, dispatch, isLoading, modalSize, postSemanticSearch, regionIds, semanticSearchHistory]
    );

    return {
        clearHistory,
        isLoading,
        semanticSearchHistory,
        submitSemanticSearch
    };
}
import {useCallback, useMemo} from "react";

import {getAnalyticsSessionId} from "@pg-mono/algolytics";

import {notifyBugsnag} from "../../errors/bugsnag/notify_bugsnag";
import {addFailureNotification} from "../../notifications/utils/add_failure_notification";
import {ISelectedRegion} from "../../offer/list/actions/offer_list_selected_data_actions";
import {useAppDispatch, useAppSelector} from "../../store/hooks";
import {isRtkqError} from "../../store/utils/is_rtkq_error";
import {usePostSemanticSearchMutation} from "../api/post_semantic_search_api";
import {postSemanticSearchCacheKey} from "../constants/post_semantic_search_cache_key";
import {getSemanticSearchModalSize} from "../tracking/get_semantic_search_modal_size";
import {hitAiAssistantCta, hitAiAssistantOffer} from "../tracking/semantic_search_gtm_events";
import {trackAmplitudeSemanticSearchViewItemList} from "../tracking/track_amplitude_semantic_search_view_item_list";
import {trackAmplitudeSubmitAiQuestion} from "../tracking/track_amplitude_submit_ai_question";
import {GetSemanticSearchStoreResponseDto} from "../types/GetSemanticSearchStoreResponseDto";
import {PostSemanticSearchDataItemDto} from "../types/PostSemanticSearchDataItemDto";
import {SemanticSearchFormValues} from "../types/SemanticSearchFormValues";
import {useSemanticSearchLocalStorage} from "./use_semantic_search_local_storage";

const SEMANTIC_SEARCH_ERROR_MESSAGES: Record<number, string> = {
    403: "Nie masz dostępu do tej funkcjonalności. Skontaktuj się z administratorem.",
    422: "Zapytanie jest nieprawidłowe. Zmień treść i spróbuj ponownie.",
    500: "Wystąpił błąd wewnętrzny. Spróbuj ponownie później.",
    502: "Wyszukiwarka jest tymczasowo niedostępna. Spróbuj ponownie za chwilę."
};

const MAX_CONVERSATION_HISTORY = 20;

function getSemanticSearchErrorMessage(status: number): string {
    return SEMANTIC_SEARCH_ERROR_MESSAGES[status] ?? "Wystąpił błąd. Spróbuj ponownie.";
}

function convertToPostSemanticSearchResultsHistory(semanticSearchHistory: GetSemanticSearchStoreResponseDto | undefined): PostSemanticSearchDataItemDto[] {
    if (!semanticSearchHistory || semanticSearchHistory.length === 0) {
        return [];
    }

    const recentHistory = semanticSearchHistory.slice(-MAX_CONVERSATION_HISTORY);

    return recentHistory.map((item) => ({
        type: item.type,
        text: item.text,
        results: item.results?.length ? item.results.map((group) => group.map((property) => property.id)) : []
    }));
}

export function useSemanticSearchSubmit() {
    const dispatch = useAppDispatch();
    const selectedRegions = useAppSelector((state) => state.offerList.selectedRegions as ISelectedRegion[]);
    const regionIds = useMemo(() => selectedRegions.map((region) => region.id), [selectedRegions]);
    const modalSize = useAppSelector((state) => getSemanticSearchModalSize(state.semanticSearchModal.surfacePhase));

    const [postSemanticSearch, {isLoading: isPostSemanticSearchLoading}] = usePostSemanticSearchMutation({fixedCacheKey: postSemanticSearchCacheKey});
    const {history: semanticSearchHistory, isLoading: isSemanticSearchHistoryLoading, addMessage, clearHistory} = useSemanticSearchLocalStorage();

    const isLoading = isPostSemanticSearchLoading || isSemanticSearchHistoryLoading;

    const submitSemanticSearch = useCallback(
        async (data: SemanticSearchFormValues) => {
            const userQuery = data.userQuery.trim();

            if (isLoading || !userQuery) return;

            const analyticsSessionId = getAnalyticsSessionId();
            const semanticSearchResultsHistory = convertToPostSemanticSearchResultsHistory(semanticSearchHistory);

            if (typeof analyticsSessionId !== "string") {
                notifyBugsnag({message: "Semantic search missing analytics session id"}, "useSemanticSearchSubmit missing analytics session id", {
                    analyticsSessionId,
                    historyLength: semanticSearchHistory?.length ?? 0,
                    regionIds
                });
            }

            if (modalSize) {
                trackAmplitudeSubmitAiQuestion(modalSize, analyticsSessionId);
            }

            addMessage({
                type: "USER_INPUT",
                text: userQuery
            });

            const semanticSearchResult = await postSemanticSearch({
                // Casting because the API requires a string, while algolytics can return an empty value.
                analytic_session_id: analyticsSessionId as string,
                data: [
                    ...semanticSearchResultsHistory,
                    {
                        type: "USER_INPUT",
                        text: userQuery,
                        results: []
                    }
                ],
                regions: regionIds
            });

            if (semanticSearchResult.error && isRtkqError(semanticSearchResult.error)) {
                const status = typeof semanticSearchResult.error.status === "number" ? semanticSearchResult.error.status : 500;

                dispatch(addFailureNotification(getSemanticSearchErrorMessage(status)));

                if (status === 403) {
                    window.location.reload();
                }

                return;
            }

            if (semanticSearchResult.data) {
                const flatProperties = semanticSearchResult.data.properties.flat();

                flatProperties.forEach((property) => hitAiAssistantOffer("view", property));

                if (modalSize && flatProperties.length > 0) {
                    trackAmplitudeSemanticSearchViewItemList({properties: flatProperties, modalSize, analyticSessionId: analyticsSessionId});
                }

                if (semanticSearchResult.data.offer_signup) {
                    hitAiAssistantCta("view", "Załóż konto");
                    hitAiAssistantCta("view", "Zaloguj się");
                }

                addMessage({
                    type: "API_RESPONSE",
                    text: semanticSearchResult.data.response_text,
                    results: semanticSearchResult.data.properties,
                    offer_signup: semanticSearchResult.data.offer_signup,
                    filters: semanticSearchResult.data.filters,
                    count: semanticSearchResult.data.count
                });
            }
        },
        [addMessage, dispatch, isLoading, modalSize, postSemanticSearch, regionIds, semanticSearchHistory]
    );

    return {
        clearHistory,
        isLoading,
        semanticSearchHistory,
        submitSemanticSearch
    };
}
